<template>
  <div class="p-3 bg-white">
    <v-tabs v-model="activeSection">
      <v-tab>Current</v-tab>
      <v-tab>History</v-tab>
    </v-tabs>
    <div v-if="activeSection === 0" class="flex items-center justify-end">
      <v-btn
        @click="toggleShowAddCampaign()"
        color="primary"
        :class="[
          $vuetify.breakpoint.lgAndUp
            ? ' white--text'
            : 'ml-3 flex-col white--text mt-2',
        ]"
      >
        Add Campaign
        <v-icon right dark>mdi-plus</v-icon>
      </v-btn>
    </div>
    <div v-if="!campaigns.length" class="flex flex-col items-center justify-center mt-72">
      <h1 class="text-gray-800 text-xl mt-2">No Campaigns, start by adding one</h1>
    </div>
    <div v-else class="mt-8">
      <v-data-table
        :headers="campaignTableHeaders"
        :items="campaigns"
        :loading="fetchingData"
        class="pl-7 pr-7 pt-4 rounded-lg"
        :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
        :expanded.sync="campaignsExpanded"
        item-key="_id"
        fixed-header
        show-expand
      >
        <template v-slot:item.id="{ item }">
          <td class="px-4 py-6 font-semibold">
            {{ item.id }}
          </td>
        </template>
        <template v-slot:item.title="{ item }">
          <td class="px-4 py-6 font-semibold">
            {{ item.title }}
          </td>
        </template>
        <template v-slot:item.status="{ item }">
          <td class="px-4 py-6 font-semibold">
            {{ item.status }}
          </td>
        </template>
        <template v-slot:item.description="{ item }">
          <td class="px-4 py-6 font-semibold">
            {{ item.description }}
          </td>
        </template>
        <template v-slot:item.startDateTime="{ item }">
          <td class="px-4 py-6 font-semibold">
            {{ formatDate(item.dateTimeStart) }}
          </td>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="bg-white px-4 py-3">
            <div class="flex items-center justify-start border border-solid border-gray-200 px-3 py-1.5">
              <p class="mb-0 w-24 font-semibold">Customers</p>
              <v-divider vertical class="mx-2" />
              <div v-if="!!item.customers" class="flex items-center gap-2 overflow-x-auto">
                <div v-for="customer in item.customers" :key="customer.phoneNo" class="bg-gray-100 px-2 py-1.5">
                  <span class="text-blue-700">{{ customer.name }}</span>
                </div>
              </div>
              <div v-else class="py-1.5">
                <span class="text-gray-500">No agents assigned</span>
              </div>
            </div>
            <div class="mt-2 flex items-center justify-start border border-solid border-gray-200 px-3 py-1.5">
              <p class="mb-0 w-24 font-semibold">Agents</p>
              <v-divider vertical class="mx-2" />
              <div v-if="!!item.channelOptions.agents" class="flex items-center gap-2 overflow-x-auto">
                <div v-for="agent in item.channelOptions.agents" :key="agent.phoneNo" class="bg-gray-100 px-2 py-1.5">
                  <span class="text-blue-700">{{ agent.name }}</span>
                </div>
              </div>
              <div v-else class="py-1.5">
                <span class="text-gray-500">No agents assigned</span>
              </div>
            </div>
          </td>
        </template>
        <template v-slot:item.action="{ item }">
          <td class="px-4">
            <div class="flex items-center gap-4">
              <v-btn
                @click="() => updateCampaignStatus({ campaignId: item._id, status: '1', indx: item.indx })"
                v-if="item.status === 'IN_PROGRESS' || item.status === 'INIT'"
                fab
                x-small
                :loading="item.updatingStatus"
                dark
              >
                <v-icon>mdi-pause</v-icon>
              </v-btn>
              <v-btn
                @click="() => updateCampaignStatus({ campaignId: item._id, status: '3', indx: item.indx })"
                v-if="item.status === 'PAUSED'"
                fab
                x-small
                dark
                :loading="item.updatingStatus"
              >
                <v-icon>mdi-play</v-icon>
              </v-btn>
              <v-btn
                @click="() => cancelCampaign({ campaignId: item._id, indx: item.indx })"
                color="red"
                elevation="2"
                v-if="item.status !== 'CANCELLED'"
                outlined
                :loading="item.cancelling"
              >Cancel</v-btn>
              <span v-else class="font-bold text-gray-600">N/A</span>
            </div>
          </td>
        </template>
      </v-data-table>
    </div>
    <v-dialog v-model="isAddCampaignVisible" width="800" persistent>
      <AddCampaign v-if="isAddCampaignVisible" @create="onCreateCampaign" @cancel="onCancelAddCampaign" />
    </v-dialog>
  </div>
</template>

<script>
import dayjs from "dayjs";
// import Channels from "@/components/Workforce/Campaign/Channels.vue";
import AddCampaign from "@/components/Workforce/Campaign/AddCampaign.vue";

export default {
  name: "CampaignsPage",
  layout: "Workforce/default",
  components: {
    AddCampaign,
  },
  data() {
    return {
      authData: this.$storage.getUniversal("token"),
      isAddCampaignVisible: false,
      activeSection: 0,
      name: "",
      description: "",
      campaigns: [],
      fetchingData: false,
      updatingCampaignStatus: false,
      deletingCampaign: false,
      campaignsExpanded: []
    };
  },
  watch: {
    activeSection: {
      handler(section) {
        if (section === 0) {
          this.fetchCurrentCampaigns();
        } else if (section === 1) {
          this.fetchCampaignsHistory();
        }
      },
      immediate: false,
    },
  },
  methods: {
    formatDate(date) {
      return dayjs(date).format('DD MMM YYYY, hh:mm a')
    },
    onCancelAddCampaign() {
      this.isAddCampaignVisible = false
    },
    toggleShowAddCampaign() {
      this.isAddCampaignVisible = !this.isAddCampaignVisible;
    },
    onCreateCampaign() {
      this.isAddCampaignVisible = false
      this.fetchCurrentCampaigns();
    },
    async cancelCampaign({ campaignId, indx }) {
      const newCampaigns = [...this.campaigns]
      newCampaigns[indx].cancelling = true;
      this.campaigns = newCampaigns;

      const response = await this.$axios.put("/workforce/campaign/status", {
        campaignId,
        status: "2",
        channelType: "call"
      }, {
        headers: {
          Authorization: this.authData.access_token,
        },
      });
      newCampaigns[indx].cancelling = false;
      newCampaigns[indx].status = "CANCELLED";
      this.campaigns = newCampaigns;
      this.$toast.success("Campaign cancelled successfully");
    },
    async updateCampaignStatus({ campaignId, status, indx }) {
      const newCampaigns = [...this.campaigns]
      newCampaigns[indx].updatingStatus = true;
      this.campaigns = newCampaigns;

      const response = await this.$axios.put("/workforce/campaign/status", {
        campaignId,
        status,
        channelType: "call"
      }, {
        headers: {
          Authorization: this.authData.access_token,
        },
      });
      newCampaigns[indx].updatingStatus = false;
      newCampaigns[indx].status = status === "1" ? "PAUSED" : "IN_PROGRESS";
      this.campaigns = newCampaigns;
      this.$toast.success(status === "1" ? "Campaign paused successfully" : "Campaign resumed successfully");
    },
    async fetchCampaignsHistory() {
      this.fetchingData = true;
      const response = await this.$axios.get("/workforce/campaign/current?history=true", {
        headers: {
          Authorization: this.authData.access_token,
        },
      });
      this.fetchingData = false;

      this.campaigns = response.data.data.map((c, i) => ({
        ...c,
        indx: i,
        id: c.channelType === "call" ? c.channelOptions?.campaignId : "-",
      }));
    },
    async fetchCurrentCampaigns() {
      this.fetchingData = true;
      const response = await this.$axios.get("/workforce/campaign/current", {
        headers: {
          Authorization: this.authData.access_token,
        },
      });
      this.fetchingData = false;

      this.campaigns = response.data.data.map((c, i) => ({
        ...c,
        indx: i,
        id: c.channelType === "call" ? c.channelOptions?.campaignId : "-",
      }));
    },
  },
  computed: {
    campaignTableHeaders() {
      const headers = [
        { text: "Campaign ID", value: "id", sortable: false },
        { text: "Campaign Name", value: "title", sortable: false },
        { text: "Campaign Description", value: "description", sortable: false },
        { text: "Start date/time", value: "startDateTime", sortable: false },
        { text: "Status", value: "status", sortable: false },
      ];

      if (this.activeSection === 0) {
        headers.push({
          text: "Action",
          value: "action",
          sortable: false,
        });
      }

      return headers;
    },
  },
  mounted() {
    this.fetchCurrentCampaigns();
  },
};
</script>

<style scoped>
</style>
